/**
 * Playbook Live session — full-duplex roleplay against a gpt-live-1 customer.
 *
 * Parallel to TrainingSessionPage, which serves knowledge-base tracks only.
 * TrainingSessionRouter sends every playbook track here.
 *
 * Runs on the live-call screen (TrainingCallLayout): the transcript in the top
 * bar, objection/FAQ entries in the conversation feed, New Inquiries in the
 * top bar, landmine/competitor alerts, and the track's playbook, all through
 * the live-call components and Redux slices. Those cards are fired by the live
 * detection services, exactly as on a call, never built by the server. The
 * rep briefing, stage and agenda progress sit in the status panel. The
 * briefing is fetched on mount so the rep can read it before pressing Start,
 * and it stays for the whole session: session_started's premise is the voice
 * model's instructions and is never shown.
 *
 * Deliberately absent: any silence threshold, end-of-turn timer or submit
 * button. Turn-taking belongs to the voice model here; adding a gate would
 * reintroduce the half-duplex feel this mode exists to remove.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getAuth, signOut } from 'firebase/auth';

import PlaybookLiveBriefing from '../components/playbookLive/PlaybookLiveBriefing';
import PlaybookLiveCompletionModal from '../components/playbookLive/PlaybookLiveCompletionModal';
import { openTrainingResults } from '../utils/trainingResults';
import TrainingCallLayout from '../components/training/TrainingCallLayout';
import TrainingStatusPanel, { TrainingCallPhase } from '../components/training/TrainingStatusPanel';
import { AlertModal } from '../components/ui/AlertModal';
import { useFirstCustomerVoice } from '../hooks/useFirstCustomerVoice';
import { useTrainingCallFeed } from '../hooks/useTrainingCallFeed';
import { useTrackRepBriefing } from '../hooks/useTrackRepBriefing';
import { useTrainingTrackPlaybook } from '../hooks/useTrainingTrackPlaybook';
import { useTurnIndicator } from '../hooks/useTurnIndicator';
import { KB_LIVE_COPY, TRAINING_CALL_COPY } from '../constants/training';
import { applyLivePlaybookFrame } from '../services/livePlaybookFrames';
import { playbookLiveAudio } from '../services/playbookLiveAudio';
import { logoutUser } from '../store/authSlice';
import { useTrainingSessionEnded } from '../hooks/useTrainingSessionEnded';
import { usableSessionId } from '../utils/trainingSessionId';
import {
  AgendaProgressData,
  CloseSignalResolvedData,
  PlaybookStage,
  SessionCompletedData,
  playbookLiveWebSocketService,
} from '../services/playbookLiveWebSocketService';

/** The rep's panel edits go to this session's checklist, as on a call. */
const sendPlaybookActionToSession = (action: { updateType: string; [key: string]: unknown }): void => {
  playbookLiveWebSocketService.sendPlaybookAction(action);
};

const NO_TRACK_MESSAGE = 'This session has no training track attached. Go back and pick a track.';
const START_FAILED_MESSAGE = 'We could not start the session. Check your microphone and try again.';

export const PlaybookLiveSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const trackId = new URLSearchParams(location.search).get('trackId');

  const [phase, setPhase] = useState<TrainingCallPhase>('preflight');
  const [stage, setStage] = useState<PlaybookStage>('S1_OPENING');
  const [agendaTotal, setAgendaTotal] = useState(0);
  const [progress, setProgress] = useState<AgendaProgressData | null>(null);
  const [signals, setSignals] = useState<CloseSignalResolvedData[]>([]);
  const [pendingSignal, setPendingSignal] = useState<string | null>(null);
  const [summary, setSummary] = useState<SessionCompletedData | null>(null);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feed = useTrainingCallFeed();
  const hasPlaybook = useTrainingTrackPlaybook(trackId);
  const briefing = useTrackRepBriefing(trackId);
  // Same guard as the knowledge base page: a launch link whose session has
  // already run (a refresh, a reopened tab) cannot run it again.
  const hasSessionEnded = useTrainingSessionEnded(usableSessionId(sessionId), true);
  const hasSessionStarted = phase === 'live' || phase === 'completed';
  // Destructured: only the stable callbacks go into dependencies, so a turn
  // change never re-creates teardown (whose unmount effect would then run).
  const { turn, noteCustomerAudio, noteMicAudio, reset: resetTurn } = useTurnIndicator();
  const teardownRef = useRef<() => Promise<void>>(async () => undefined);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Live only once the customer is heard: session_started lands seconds
  // before the first word (see useFirstCustomerVoice).
  const voice = useFirstCustomerVoice({
    onVoice: () => setPhase((current) => (current === 'connecting' ? 'live' : current)),
    onTimeout: () => {
      setError(TRAINING_CALL_COPY.customerSilent);
      setPhase('preflight');
      void teardownRef.current();
    },
  });

  const teardown = useCallback(async () => {
    voice.disarm();
    resetTurn();
    playbookLiveWebSocketService.disconnect();
    await playbookLiveAudio.stop();
  }, [resetTurn, voice]);
  teardownRef.current = teardown;

  const handleStart = useCallback(async () => {
    if (!trackId) {
      setError(NO_TRACK_MESSAGE);
      return;
    }
    feed.resetFeed();
    resetTurn();
    setPhase('connecting');
    voice.arm();
    setIsEnding(false);
    setError(null);

    playbookLiveWebSocketService.setHandlers({
      onSessionStarted: (data) => {
        setAgendaTotal(data?.agendaTotal ?? 0);
      },
      onStageChanged: (data) => setStage(data.new_phase),
      onTranscriptDelta: (data) => {
        feed.appendTranscript(data.speaker, data.text);
        if (data.speaker === 'ai_customer') voice.noteCustomerText(data.text);
      },
      onDiscoveryTurn: () => feed.flushTranscript(),
      // Every card comes from the live detection services, as on a call.
      onQuestionDetected: (data) => feed.liveDetection('question_detected', data),
      onStoredAnswerGenerated: (data) => feed.liveDetection('stored_answer_generated', data),
      onAnswerGenerated: (data) => feed.liveDetection('answer_generated', data),
      onAnswerError: (data) => feed.liveDetection('answer_error', data),
      onLandmineAlert: (data) => feed.liveDetection('landmine_alert', data),
      onCompetitorAlert: (data) => feed.liveDetection('competitor_alert', data),
      // The track's playbook panel ticks and notes as on a call.
      onPlaybookFrame: (type, data) => applyLivePlaybookFrame(dispatch, type, data),
      onAgendaProgress: (data) => {
        setProgress(data);
        feed.flushTranscript();
      },
      onCloseSignal: (data) => setPendingSignal(data.type),
      onCloseSignalResolved: (data) => {
        setPendingSignal(null);
        setSignals((current) => [...current, data]);
      },
      onSessionCompleted: (data) => {
        feed.flushTranscript();
        setSummary(data);
        setIsEnding(false);
        voice.disarm();
        resetTurn();
        setPhase('completed');
        void playbookLiveAudio.stop();
      },
      onCustomerAudio: (pcm) => {
        playbookLiveAudio.playChunk(pcm);
        voice.noteCustomerAudio(pcm);
        noteCustomerAudio(pcm);
      },
      onError: (message) => {
        setError(message);
        // A server rejection while connecting must not leave the rep on the
        // connecting spinner until the voice timeout.
        if (phaseRef.current === 'connecting') {
          setPhase('preflight');
          void teardownRef.current();
        }
      },
    });

    try {
      await playbookLiveAudio.start({
        onMicChunk: (pcm) => {
          playbookLiveWebSocketService.sendAudio(pcm);
          noteMicAudio(pcm);
        },
      });
      await playbookLiveWebSocketService.connect(
        trackId,
        usableSessionId(sessionId)
      );
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : START_FAILED_MESSAGE);
      setPhase('preflight');
      await teardown();
    }
  }, [dispatch, feed, noteCustomerAudio, noteMicAudio, resetTurn, sessionId, teardown, trackId, voice]);

  const handleEnd = useCallback(async () => {
    setIsEnding(true);
    playbookLiveWebSocketService.endSession();
    resetTurn();
    await playbookLiveAudio.stop();
  }, [resetTurn]);

  const handleSignOut = useCallback(async () => {
    // Tear the live session down first: signing out mid-call would otherwise
    // leave the microphone open and the socket billing.
    await teardown();
    await signOut(getAuth());
    dispatch(logoutUser() as never);
    navigate('/login');
  }, [dispatch, navigate, teardown]);

  useEffect(() => () => { void teardown(); }, [teardown]);

  const statusPanel = (
    <TrainingStatusPanel
      phase={phase}
      onStart={handleStart}
      onEnd={handleEnd}
      isEnding={isEnding}
      turn={turn}
    >
      {(hasSessionStarted || briefing) && (
        <PlaybookLiveBriefing
          briefing={briefing}
          showsSessionProgress={hasSessionStarted}
          stage={stage}
          progress={progress}
          agendaTotal={agendaTotal}
          pendingSignal={pendingSignal}
          signals={signals}
        />
      )}
    </TrainingStatusPanel>
  );

  return (
    <TrainingCallLayout
      onSignOut={handleSignOut}
      statusPanel={statusPanel}
      showPlaybook={hasPlaybook}
      isSessionActive={phase === 'live'}
      onPlaybookAction={sendPlaybookActionToSession}
    >
      {error && <AlertModal isOpen message={error} onClose={() => setError(null)} />}
      <AlertModal isOpen={hasSessionEnded} message={KB_LIVE_COPY.alreadyEnded} onClose={handleSignOut} />
      {phase === 'completed' && summary && (
        <PlaybookLiveCompletionModal summary={summary} onViewResults={openTrainingResults} />
      )}
    </TrainingCallLayout>
  );
};

export default PlaybookLiveSessionPage;
